<template>
  <div class="occupancy-map-view">
    <!-- Map Container -->
    <div class="map-container">
      <div v-if="isLoading" class="map-loading">
        <div class="loading-spinner"></div>
        <p>Loading map...</p>
      </div>
      
      <l-map
        ref="map"
        :zoom="mapZoom"
        :maxZoom="20"
        :center="mapCenter"
        :options="mapOptions"
        class="leaflet-map"
        @ready="onMapReady"
        @update:zoom="onZoomUpdate"
        @update:center="onCenterUpdate"
      >
        <!-- Base tile layer -->
        <l-tile-layer
          :url="tileUrl"
          :attribution="attribution"
          :options="tileOptions"
        />
        
        <!-- User markers -->
        <l-marker
          v-for="location in validLocations"
          :key="`marker-${location.device_id}-${location.ts}`"
          :lat-lng="[location.lat, location.lon]"
          :icon="getMarkerIcon(location)"
          @click="onMarkerClick(location)"
        >
          <l-popup :options="{ maxWidth: 320, className: 'custom-popup' }">
            <div class="popup-content">
              <div class="popup-header">
                <div class="user-avatar">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"/>
                  </svg>
                </div>
                <div class="user-info">
                  <div class="user-name">{{ location.device_id }}</div>
                  <div class="user-device">{{ (location.device_id || '').split(':')[0] }}</div>
                </div>
                <div class="status-badge" :class="{ moving: location.is_moving }">
                  {{ location.is_moving ? 'Moving' : 'Stationary' }}
                </div>
              </div>
              
              <div class="popup-details">
                <div class="detail-row">
                  <span class="label">Location</span>
                  <span class="value">{{ location.lat.toFixed(6) }}, {{ location.lon.toFixed(6) }}</span>
                </div>
                
                <div class="detail-row">
                  <span class="label">Accuracy</span>
                  <span class="value accuracy" :class="getAccuracyClass(location.accuracy)">
                    {{ Number.isFinite(location.accuracy) ? `${location.accuracy.toFixed(1)}m` : 'N/A' }}
                  </span>
                </div>
                
                <div v-if="Number.isFinite(location.altitude)" class="detail-row">
                  <span class="label">Altitude</span>
                  <span class="value">{{ location.altitude.toFixed(1) }}m</span>
                </div>
                
                <div v-if="Number.isFinite(location.speed)" class="detail-row">
                  <span class="label">Speed</span>
                  <span class="value">{{ (location.speed * 3.6).toFixed(1) }} km/h</span>
                </div>
                
                <div class="detail-row">
                  <span class="label">Last seen</span>
                  <span class="value time" :class="getTimeAgoClass(location.ts)">
                    {{ getTimeAgo(location.ts) }}
                  </span>
                </div>
              </div>
              
              <div class="popup-actions">
                <button class="popup-btn" @click="viewUserHistory(location.device_id)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.5,8H12V13L16.28,15.54L17,14.33L13.5,12.25V8M13,3A9,9 0 0,0 4,12H1L4.96,16.03L9,12H6A7,7 0 0,1 13,5A7,7 0 0,1 20,12A7,7 0 0,1 13,19C11.07,19 9.32,18.21 8.06,16.94L6.64,18.36C8.27,20 10.5,21 13,21A9,9 0 0,0 22,12A9,9 0 0,0 13,3"/>
                  </svg>
                  History
                </button>
                <button class="popup-btn" @click="openInMaps(location)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2C15.31,2 18,4.66 18,7.95C18,12.41 12,19 12,19C12,19 6,12.41 6,7.95C6,4.66 8.69,2 12,2M12,6A2,2 0 0,0 10,8A2,2 0 0,0 12,10A2,2 0 0,0 14,8A2,2 0 0,0 12,6Z"/>
                  </svg>
                  Maps
                </button>
              </div>
            </div>
          </l-popup>
        </l-marker>
      </l-map>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, defineProps, defineEmits, defineExpose } from 'vue'
import { LMap, LTileLayer, LMarker, LPopup } from '@vue-leaflet/vue-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Props
const props = defineProps({
  locations: {
    type: Array,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  centerOnUser: {
    type: Object,
    default: null
  }
})

// Emits
const emit = defineEmits(['user-selected', 'map-ready'])

// Reactive state
const map = ref(null)
const mapZoom = ref(15)
const mapCenter = ref([32.7767, -96.7970]) // Dallas, TX default
const highlightedUser = ref(null)
const hasUserInteracted = ref(false)
const isInitialLoad = ref(true)
const lastZoomLevel = ref(15)

// Map configuration
const tileUrl = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
const attribution = 'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
const mapOptions = {
  zoomControl: true,
  attributionControl: true,
  scrollWheelZoom: true,
  doubleClickZoom: true,
  dragging: true
}
const tileOptions = {
  maxZoom: 20,
  minZoom: 10,
  keepBuffer: 4,
  updateWhenZooming: false,
  updateWhenIdle: true
}

// Computed properties
const validLocations = computed(() => {
  return props.locations.filter(location => 
    location && 
    Number.isFinite(location.lat) && 
    Number.isFinite(location.lon) &&
    Math.abs(location.lat) <= 90 && 
    Math.abs(location.lon) <= 180
  )
})

const activeUsers = computed(() => {
  const now = Date.now()
  return validLocations.value.filter(location => {
    const ts = location?.ts
    if (!ts) return false
    const lastSeen = new Date(ts).getTime()
    return Number.isFinite(lastSeen) && (now - lastSeen) < 300000 // 5 minutes
  }).length
})

const movingUsers = computed(() => {
  return validLocations.value.filter(location => location.is_moving).length
})

const stationaryUsers = computed(() => {
  return validLocations.value.filter(location => !location.is_moving).length
})

// Utility functions
function getTimeAgo(timestamp) {
  if (!timestamp) return 'Unknown'
  const then = new Date(timestamp).getTime()
  if (!Number.isFinite(then)) return 'Unknown'
  const diffMinutes = Math.floor((Date.now() - then) / (1000 * 60))
  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

function getTimeAgoClass(timestamp) {
  if (!timestamp) return 'old'
  const then = new Date(timestamp).getTime()
  if (!Number.isFinite(then)) return 'old'
  const diffMinutes = Math.floor((Date.now() - then) / (1000 * 60))
  if (diffMinutes < 5) return 'fresh'
  if (diffMinutes < 30) return 'recent'
  return 'old'
}

function getAccuracyClass(accuracy) {
  if (!Number.isFinite(accuracy)) return 'unknown'
  if (accuracy <= 5) return 'excellent'
  if (accuracy <= 15) return 'good'
  return 'poor'
}

function getMarkerIcon(location) {
  const isMoving = location.is_moving
  const isRecent = getTimeAgoClass(location.ts) === 'fresh'
  const isHighlighted = highlightedUser.value === location.device_id
  
  const iconHtml = `
    <div class="custom-marker ${isMoving ? 'moving' : 'stationary'} ${isRecent ? 'recent' : ''} ${isHighlighted ? 'highlighted' : ''}">
      <div class="marker-inner">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.5 1h-8C6.12 1 5 2.12 5 3.5v17C5 21.88 6.12 23 7.5 23h8c1.38 0 2.5-1.12 2.5-2.5v-17C18 2.12 16.88 1 15.5 1zm-4 21c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4.5-4H7v-14h9v14z"/>
        </svg>
      </div>
    </div>
  `
  
  return L.divIcon({
    html: iconHtml,
    className: 'leaflet-custom-marker',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -9]
  })
}

// Map event handlers
function onZoomUpdate(zoom) {
  lastZoomLevel.value = mapZoom.value
  mapZoom.value = zoom
  if (!isInitialLoad.value) {
    hasUserInteracted.value = true
  }
  
  // Force tile refresh on zoom
  nextTick(() => {
    if (map.value?.leafletObject) {
      map.value.leafletObject.invalidateSize()
    }
  })
}

function onCenterUpdate(center) {
  mapCenter.value = center
  if (!isInitialLoad.value) {
    hasUserInteracted.value = true
  }
}

function onMapReady() {
  if (map.value?.leafletObject) {
    console.log('Map ready')
    
    const mapInstance = map.value.leafletObject
    
    // Fix tiles loading issue with multiple checks
    const fixTiles = () => {
      mapInstance.invalidateSize()
      // Force tile layer to reload
      mapInstance.eachLayer((layer) => {
        if (layer._url) {
          layer.redraw()
        }
      })
    }
    
    setTimeout(fixTiles, 100)
    setTimeout(fixTiles, 300)
    setTimeout(fixTiles, 500)
    
    // Add event listeners to track user interactions
    mapInstance.on('dragstart', () => {
      if (!isInitialLoad.value) {
        hasUserInteracted.value = true
      }
    })
    
    mapInstance.on('zoomstart', () => {
      if (!isInitialLoad.value) {
        hasUserInteracted.value = true
      }
    })
    
    // Fix tiles after zoom ends
    mapInstance.on('zoomend', () => {
      setTimeout(() => {
        mapInstance.invalidateSize()
      }, 50)
    })
    
    // Only center on initial load
    if (isInitialLoad.value) {
      centerMapOnData()
      isInitialLoad.value = false
    }
    
    emit('map-ready')
  }
}

function centerMapOnData() {
  if (!map.value?.leafletObject || validLocations.value.length === 0) return
  
  if (validLocations.value.length === 1) {
    const loc = validLocations.value[0]
    map.value.leafletObject.setView([loc.lat, loc.lon], 18)
  } else {
    const bounds = L.latLngBounds(
      validLocations.value.map(location => [location.lat, location.lon])
    )
    map.value.leafletObject.fitBounds(bounds, {
      padding: [50, 50]
    })
  }
}

function centerOnLocation(lat, lon) {
  if (!map.value?.leafletObject) return
  
  map.value.leafletObject.setView([lat, lon], 16, {
    animate: true,
    duration: 1
  })
  hasUserInteracted.value = true
}

function highlightUser(userId) {
  highlightedUser.value = userId
  setTimeout(() => {
    highlightedUser.value = null
  }, 5000)
}

function resetMapInteraction() {
  hasUserInteracted.value = false
  centerMapOnData()
}

// Event handlers
function onMarkerClick(location) {
  console.log('Marker clicked:', location.device_id)
}

function viewUserHistory(userId) {
  emit('user-selected', userId)
}

function openInMaps(location) {
  const url = `https://www.google.com/maps?q=${location.lat},${location.lon}`
  window.open(url, '_blank')
}

// Watchers
watch(() => props.locations, () => {
  nextTick(() => {
    if (validLocations.value.length > 0 && !hasUserInteracted.value && !isInitialLoad.value) {
      centerMapOnData()
    }
    // Fix tiles after data update
    if (map.value?.leafletObject) {
      setTimeout(() => {
        map.value.leafletObject.invalidateSize()
      }, 100)
    }
  })
}, { deep: true })

watch(() => props.centerOnUser, (newVal) => {
  if (newVal && map.value?.leafletObject) {
    centerOnLocation(newVal.lat, newVal.lon)
    highlightUser(newVal.device_id)
  }
})

// Expose methods
defineExpose({
  centerOnLocation,
  highlightUser,
  resetMapInteraction
})

// Lifecycle
onMounted(() => {
  // Force Leaflet to invalidate size after mount with multiple retries
  const fixMapSize = () => {
    if (map.value?.leafletObject) {
      map.value.leafletObject.invalidateSize()
    }
  }
  
  nextTick(() => {
    setTimeout(fixMapSize, 100)
    setTimeout(fixMapSize, 300)
    setTimeout(fixMapSize, 600)
    setTimeout(fixMapSize, 1000)
  })
  
  // Custom marker styles
  const style = document.createElement('style')
  style.textContent = `
    .leaflet-custom-marker {
      background: transparent !important;
      border: none !important;
    }
    
    .custom-marker {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
      border: 2px solid white;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      cursor: pointer;
    }
    
    .custom-marker.moving {
      background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
      color: white;
    }
    
    .custom-marker.stationary {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      color: white;
    }
    
    .custom-marker.recent {
      animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }
    
    .custom-marker.highlighted {
      animation: highlight 2s ease-in-out;
      transform: scale(1.5);
      z-index: 1000 !important;
      box-shadow: 0 0 20px rgba(74, 144, 226, 0.8);
    }
    
    @keyframes pulse {
      0%, 100% { 
        transform: scale(1);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
      }
      50% { 
        transform: scale(1.1);
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.4);
      }
    }
    
    @keyframes highlight {
      0% { 
        transform: scale(1.5);
        box-shadow: 0 0 20px rgba(74, 144, 226, 0.8);
      }
      50% { 
        transform: scale(1.8);
        box-shadow: 0 0 30px rgba(74, 144, 226, 1);
      }
      100% { 
        transform: scale(1.5);
        box-shadow: 0 0 20px rgba(74, 144, 226, 0.8);
      }
    }
    
    .custom-marker:hover {
      transform: scale(1.3);
      box-shadow: 0 3px 12px rgba(0, 0, 0, 0.4);
    }
    
    .marker-inner {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `
  document.head.appendChild(style)
})
</script>

<style scoped>
.occupancy-map-view {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Map Container */
.map-container {
  flex: 1;
  position: relative;
  background: white;
  overflow: hidden;
}

.leaflet-map {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

/* Fix for washed out tiles */
:deep(.leaflet-container) {
  background: #f1f5f9 !important;
  font-family: inherit;
}

:deep(.leaflet-tile-pane) {
  opacity: 1 !important;
}

:deep(.leaflet-tile-container) {
  opacity: 1 !important;
}

:deep(.leaflet-tile) {
  opacity: 1 !important;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}

:deep(.leaflet-fade-anim .leaflet-tile) {
  will-change: auto !important;
  transition: none !important;
}

:deep(.leaflet-zoom-anim .leaflet-zoom-animated) {
  will-change: auto !important;
}

:deep(.leaflet-control-zoom) {
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

:deep(.leaflet-control-zoom a) {
  color: #334155 !important;
  border: none !important;
  width: 32px !important;
  height: 32px !important;
  line-height: 32px !important;
  font-size: 18px !important;
}

:deep(.leaflet-control-zoom a:hover) {
  background: #f1f5f9 !important;
}

.map-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #4facfe;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.map-loading p {
  margin-top: 1rem;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Popup Styles */
:deep(.custom-popup) {
  border-radius: 16px;
  overflow: hidden;
}

:deep(.custom-popup .leaflet-popup-content-wrapper) {
  border-radius: 16px;
  padding: 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid #e2e8f0;
}

:deep(.custom-popup .leaflet-popup-content) {
  margin: 0;
  line-height: 1.5;
}

:deep(.custom-popup .leaflet-popup-tip) {
  border-top-color: white;
}

.popup-content {
  padding: 1.25rem;
  min-width: 280px;
}

.popup-header {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9375rem;
  margin-bottom: 0.125rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-device {
  font-size: 0.8125rem;
  color: #64748b;
  text-transform: capitalize;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  color: #0284c7;
  flex-shrink: 0;
}

.status-badge.moving {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
}

.popup-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.label {
  font-size: 0.8125rem;
  color: #64748b;
  font-weight: 500;
}

.value {
  font-size: 0.8125rem;
  color: #1e293b;
  font-weight: 600;
  text-align: right;
}

.value.accuracy.excellent {
  color: #16a34a;
}

.value.accuracy.good {
  color: #f59e0b;
}

.value.accuracy.poor {
  color: #dc2626;
}

.value.time.fresh {
  color: #16a34a;
}

.value.time.recent {
  color: #f59e0b;
}

.value.time.old {
  color: #dc2626;
}

.popup-actions {
  display: flex;
  gap: 0.5rem;
}

.popup-btn {
  flex: 1;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 0.625rem 0.875rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.25);
}

.popup-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.popup-btn:active {
  transform: translateY(0);
}

/* Responsive Design - 1200px */
@media (max-width: 1200px) {
  .leaflet-map {
    min-height: 350px;
  }
}

/* Responsive Design - 965px */
@media (max-width: 965px) {
  .leaflet-map {
    min-height: 350px;
  }
}

/* Responsive Design - 765px */
@media (max-width: 765px) {
  .popup-content {
    min-width: 260px;
    padding: 1rem;
  }
  
  .user-avatar {
    width: 36px;
    height: 36px;
  }
  
  .user-avatar svg {
    width: 16px;
    height: 16px;
  }
  
  .leaflet-map {
    min-height: 300px;
  }
}

/* Responsive Design - 655px */
@media (max-width: 655px) {
  .popup-content {
    min-width: 240px;
    padding: 0.875rem;
  }
 
  .popup-content {
    min-width: 260px;
    padding: 1rem;
  }
  
  .user-avatar {
    width: 36px;
    height: 36px;
  }
  
  .user-avatar svg {
    width: 16px;
    height: 16px;
  }
 }

/* Responsive Design - 655px */
@media (max-width: 655px) {
  .map-header {
    padding: 0.875rem;
  }
  
  .stats-container {
    gap: 0.5rem;
  }
  
  .stat-card {
    padding: 0.625rem;
    gap: 0.625rem;
    border-radius: 10px;
  }
  
  .stat-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }
  
  .stat-icon svg {
    width: 14px;
    height: 14px;
  }
  
  .stat-value {
    font-size: 1.125rem;
  }
  
  .stat-label {
    font-size: 0.6875rem;
  }
  
  .popup-content {
    min-width: 240px;
    padding: 0.875rem;
  }
  
  .popup-header {
    gap: 0.625rem;
    margin-bottom: 0.875rem;
    padding-bottom: 0.875rem;
  }
  
  .user-name {
    font-size: 0.875rem;
  }
  
  .user-device {
    font-size: 0.75rem;
  }
  
  .status-badge {
    padding: 0.25rem 0.625rem;
    font-size: 0.6875rem;
  }
  
  .popup-details {
    gap: 0.625rem;
    margin-bottom: 0.875rem;
  }
  
  .label, .value {
    font-size: 0.75rem;
  }
  
  .popup-btn {
    padding: 0.5rem 0.625rem;
    font-size: 0.75rem;
  }
  
  .popup-btn svg {
    width: 14px;
    height: 14px;
  }
}

/* Responsive Design - 465px */
@media (max-width: 465px) {
  .occupancy-map-view {
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for mobile */
  }
  
  .map-header {
    padding: 0.75rem;
  }
  
  .stats-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
  
  .stat-card {
    padding: 0.5rem;
    gap: 0.5rem;
    flex-direction: column;
    text-align: center;
    border-radius: 8px;
  }
  
  .stat-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    margin: 0 auto;
  }
  
  .stat-icon svg {
    width: 12px;
    height: 12px;
  }
  
  .stat-value {
    font-size: 1rem;
  }
  
  .stat-label {
    font-size: 0.625rem;
    white-space: nowrap;
  }
  
  .leaflet-map {
    min-height: 300px;
  }
  
  .popup-content {
    min-width: 220px;
    padding: 0.75rem;
  }
  
  .popup-header {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .user-avatar {
    width: 32px;
    height: 32px;
  }
  
  .user-info {
    flex: 1 1 100%;
    order: 2;
  }
  
  .status-badge {
    order: 1;
    margin-left: auto;
  }
  
  .popup-actions {
    flex-direction: column;
    gap: 0.375rem;
  }
  
  .popup-btn {
    padding: 0.5rem;
    font-size: 0.6875rem;
  }
}

/* Responsive Design - 355px */
@media (max-width: 355px) {
  .map-header {
    padding: 0.625rem;
  }
  
  .stats-container {
    gap: 0.375rem;
  }
  
  .stat-card {
    padding: 0.375rem;
    gap: 0.375rem;
  }
  
  .stat-icon {
    width: 24px;
    height: 24px;
    border-radius: 4px;
  }
  
  .stat-icon svg {
    width: 10px;
    height: 10px;
  }
  
  .stat-value {
    font-size: 0.875rem;
  }
  
  .stat-label {
    font-size: 0.5625rem;
  }
  
  .leaflet-map {
    min-height: 250px;
  }
  
  .popup-content {
    min-width: 200px;
    padding: 0.625rem;
  }
  
  .popup-header {
    margin-bottom: 0.625rem;
    padding-bottom: 0.625rem;
  }
  
  .user-avatar {
    width: 28px;
    height: 28px;
  }
  
  .user-avatar svg {
    width: 14px;
    height: 14px;
  }
  
  .user-name {
    font-size: 0.8125rem;
  }
  
  .user-device {
    font-size: 0.6875rem;
  }
  
  .status-badge {
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
  }
  
  .popup-details {
    gap: 0.5rem;
    margin-bottom: 0.625rem;
  }
  
  .label, .value {
    font-size: 0.6875rem;
  }
  
  .popup-btn {
    padding: 0.375rem;
    font-size: 0.625rem;
    gap: 0.25rem;
  }
  
  .popup-btn svg {
    width: 12px;
    height: 12px;
  }
  
  :deep(.leaflet-control-zoom a) {
    width: 28px !important;
    height: 28px !important;
    line-height: 28px !important;
    font-size: 16px !important;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .stat-card:hover {
    transform: none;
  }
  
  .popup-btn:hover {
    transform: none;
  }
  
  .popup-btn:active {
    transform: scale(0.95);
  }
  
  .custom-marker:hover {
    transform: none;
  }
}

/* Landscape mobile optimizations */
@media (max-height: 500px) and (orientation: landscape) {
  .map-header {
    padding: 0.5rem;
  }
  
  .stats-container {
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
  }
  
  .stat-card {
    padding: 0.375rem;
    gap: 0.25rem;
  }
  
  .stat-icon {
    width: 24px;
    height: 24px;
  }
  
  .stat-value {
    font-size: 0.875rem;
  }
  
  .stat-label {
    font-size: 0.5625rem;
  }
  
  .leaflet-map {
    min-height: 200px;
  }
}

/* Print styles */
@media print {
  .map-header {
    break-inside: avoid;
  }
  
  .popup-actions {
    display: none;
  }
}
</style>